import { Link } from 'react-router-dom';
import styles from './Terms.module.css';

export default function Terms() {
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Link to="/" className={styles.backLink}>&larr; Back to Vennio</Link>

        <h1 className={styles.title}>Terms of Service</h1>
        <p className={styles.date}>Last updated: 5 May 2026</p>

        <div className={styles.prose}>

          <section>
            <h2>1. Definitions</h2>
            <p>In these Terms of Service:</p>
            <ul>
              <li><strong>"Vennio," "we," "us," or "our"</strong> refers to Whisker One Ltd, a company incorporated in England and Wales (company number 16270781), trading as Vennio, a scheduling infrastructure platform.</li>
              <li><strong>"Service"</strong> refers to the Vennio platform, website, APIs, and all related services.</li>
              <li><strong>"User," "you," or "your"</strong> refers to any individual or entity that accesses or uses the Service.</li>
              <li><strong>"Calendar Data"</strong> refers to calendar events, availability information, and related metadata accessed through connected calendar accounts.</li>
              <li><strong>"Connected Accounts"</strong> refers to third-party calendar services (such as Google Calendar or Microsoft Outlook) that you authorise Vennio to access.</li>
            </ul>
          </section>

          <section>
            <h2>2. Acceptance of Terms</h2>
            <p>
              By accessing or using Vennio, you agree to be bound by these Terms of Service and our Privacy Policy.
              If you do not agree to these terms, please do not use the Service.
            </p>
            <p>
              You must be at least 16 years of age to use the Service. By using the Service, you represent and
              warrant that you meet this age requirement.
            </p>
          </section>

          <section>
            <h2>3. Description of Service</h2>
            <p>
              Vennio is a demand-side scheduling infrastructure platform that inverts traditional booking models.
              Rather than publishing availability for others to book, Vennio enables businesses to:
            </p>
            <ul>
              <li><strong>Aggregate availability</strong>: Connect multiple calendars across Google and Microsoft platforms to consolidate availability in one place</li>
              <li><strong>View shared availability</strong>: See when contacts and customers are available, with their consent</li>
              <li><strong>Propose appointments</strong>: Proactively suggest meeting times based on mutual availability</li>
              <li><strong>Coordinate schedules</strong>: Facilitate scheduling between multiple parties without back-and-forth communication</li>
              <li><strong>Integrate with business tools</strong>: Connect with CRM systems and other business applications via our API</li>
            </ul>
            <p>
              The Service operates on a consent-based model where all parties must authorise calendar access before
              their availability is shared.
            </p>
          </section>

          <section>
            <h2>4. User Accounts</h2>
            <h3>4.1 Account Creation</h3>
            <p>
              To use certain features of the Service, you must create an account by authenticating through Google OAuth
              or Microsoft OAuth. You are responsible for:
            </p>
            <ul>
              <li>Maintaining the confidentiality and security of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Providing accurate, current, and complete information</li>
              <li>Promptly updating your information if it changes</li>
            </ul>
            <h3>4.2 Account Security</h3>
            <p>
              You must notify us immediately at{' '}
              <a href="mailto:matt@vennio.app">matt@vennio.app</a>{' '}
              if you become aware of any unauthorised access to or use of your account.
            </p>
          </section>

          <section>
            <h2>5. Calendar Access and Data Use</h2>
            <h3>5.1 Authorisation</h3>
            <p>
              When you connect your calendar accounts, you grant Vennio permission to access your Calendar Data as
              described in our Privacy Policy. This access is used solely to provide the Service's scheduling functionality.
            </p>
            <h3>5.2 Google API Services</h3>
            <p>
              Vennio's use and transfer of information received from Google APIs adheres to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
                Google API Services User Data Policy
              </a>, including the Limited Use requirements. Specifically:
            </p>
            <ul>
              <li>We only request access to the calendar data necessary to provide scheduling features</li>
              <li>We do not use Google user data for serving advertisements</li>
              <li>We do not allow humans to read your data unless we have your affirmative agreement, it is necessary for security purposes, to comply with applicable law, or our use is limited to internal operations</li>
              <li>We do not transfer your data to third parties except as necessary to provide the Service, as required by law, or with your explicit consent</li>
            </ul>
            <h3>5.3 Microsoft API Services</h3>
            <p>
              When connecting Microsoft accounts, Vennio complies with Microsoft's API Terms of Use and Graph API policies.
            </p>
            <h3>5.4 Revoking Access</h3>
            <p>You can revoke Vennio's access to your calendar data at any time by:</p>
            <ul>
              <li>Disconnecting the calendar in your Vennio account settings</li>
              <li>Revoking access through your Google Account permissions (myaccount.google.com/permissions)</li>
              <li>Revoking access through your Microsoft Account permissions</li>
            </ul>
            <h3>5.5 Data Retention</h3>
            <p>When you disconnect a calendar or delete your account:</p>
            <ul>
              <li>We will delete your Calendar Data from our active systems within 30 days</li>
              <li>Backup copies may be retained for up to 90 days before being permanently deleted</li>
              <li>We may retain anonymised, aggregated data that cannot identify you</li>
            </ul>
          </section>

          <section>
            <h2>6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
              <li>Attempt to gain unauthorised access to the Service, other users' accounts, or our systems</li>
              <li>Interfere with, disrupt, or place an undue burden on the Service or its infrastructure</li>
              <li>Send spam, unsolicited communications, or marketing messages through the Service</li>
              <li>Impersonate others or misrepresent your affiliation with any person or entity</li>
              <li>Collect, harvest, or store personal data about other users without their explicit consent</li>
              <li>Use the Service to schedule meetings for fraudulent, deceptive, or malicious purposes</li>
              <li>Reverse engineer, decompile, or attempt to extract the source code of the Service</li>
              <li>Use automated means (bots, scrapers, etc.) to access the Service without our written permission</li>
              <li>Resell or redistribute the Service without authorisation</li>
            </ul>
          </section>

          <section>
            <h2>7. Payment Terms</h2>
            <h3>7.1 Subscription Plans</h3>
            <p>
              Certain features of the Service require a paid subscription. Pricing, features, and billing cycles
              are described on our website and may change from time to time.
            </p>
            <h3>7.2 Billing</h3>
            <p>
              By subscribing to a paid plan, you authorise us to charge the applicable fees to your designated
              payment method. Fees are billed in advance on a monthly or annual basis, depending on your chosen plan.
            </p>
            <h3>7.3 Refunds</h3>
            <p>
              Subscription fees are generally non-refundable, except where required by applicable law or at our sole discretion.
            </p>
            <h3>7.4 Price Changes</h3>
            <p>
              We may change our prices at any time. For existing subscribers, price changes will take effect at the
              start of the next billing cycle following notice of the change.
            </p>
          </section>

          <section>
            <h2>8. Intellectual Property</h2>
            <h3>8.1 Our Intellectual Property</h3>
            <p>
              The Service, including its original content, features, functionality, design, and underlying technology,
              is owned by Vennio and is protected by United Kingdom and international copyright, trademark, patent,
              trade secret, and other intellectual property laws.
            </p>
            <h3>8.2 Licence to Use</h3>
            <p>
              Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable licence to
              access and use the Service for your personal or internal business purposes.
            </p>
            <h3>8.3 Restrictions</h3>
            <p>
              You may not copy, modify, distribute, sell, lease, or create derivative works based on the Service
              without our prior written consent.
            </p>
          </section>

          <section>
            <h2>9. User Content</h2>
            <h3>9.1 Ownership</h3>
            <p>
              You retain ownership of any content you submit, post, or display through the Service ("User Content").
            </p>
            <h3>9.2 Licence Grant</h3>
            <p>
              By submitting User Content, you grant Vennio a worldwide, non-exclusive, royalty-free licence to use,
              store, reproduce, modify, and display that content solely as necessary to provide and improve the Service.
            </p>
            <h3>9.3 Responsibility</h3>
            <p>
              You are solely responsible for your User Content and represent that you have all necessary rights to
              grant the licence above.
            </p>
          </section>

          <section>
            <h2>10. Third-Party Integrations</h2>
            <h3>10.1 Third-Party Services</h3>
            <p>
              The Service integrates with third-party services including Google Calendar, Microsoft Outlook, HubSpot,
              Salesforce, and others. Your use of these integrations is subject to the respective third party's terms
              of service and privacy policies.
            </p>
            <h3>10.2 No Endorsement</h3>
            <p>
              Integration with third-party services does not imply endorsement by or affiliation with those services.
              We are not responsible for the availability, accuracy, or practices of third-party services.
            </p>
          </section>

          <section>
            <h2>11. Disclaimer of Warranties</h2>
            <p className={styles.legal}>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
              IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, TITLE, AND NON-INFRINGEMENT.
            </p>
            <p className={styles.legal}>WE DO NOT WARRANT THAT:</p>
            <ul>
              <li>The Service will be uninterrupted, timely, secure, or error-free</li>
              <li>The results obtained from the Service will be accurate or reliable</li>
              <li>Any errors in the Service will be corrected</li>
            </ul>
          </section>

          <section>
            <h2>12. Limitation of Liability</h2>
            <p className={styles.legal}>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, VENNIO AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND
              AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
              INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, RESULTING FROM:
            </p>
            <ul>
              <li>Your access to or use of (or inability to access or use) the Service</li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Unauthorised access, use, or alteration of your transmissions or content</li>
              <li>Any scheduling errors, missed appointments, or calendar synchronisation issues</li>
            </ul>
            <p className={styles.legal}>
              IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU HAVE PAID TO US IN THE TWELVE (12) MONTHS
              PRECEDING THE CLAIM, OR ONE HUNDRED POUNDS (£100), WHICHEVER IS GREATER.
            </p>
            <p>
              Nothing in these Terms shall exclude or limit liability for death or personal injury caused by negligence,
              fraud, or any other liability that cannot be excluded by law.
            </p>
          </section>

          <section>
            <h2>13. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Vennio and its officers, directors, employees, agents,
              and affiliates from and against any claims, liabilities, damages, losses, costs, and expenses (including
              reasonable legal fees) arising out of or relating to:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your User Content</li>
            </ul>
          </section>

          <section>
            <h2>14. Termination</h2>
            <h3>14.1 Termination by You</h3>
            <p>
              You may terminate your account at any time by deleting your account through the Service settings or by
              contacting us at{' '}
              <a href="mailto:matt@vennio.app">matt@vennio.app</a>.
            </p>
            <h3>14.2 Termination by Us</h3>
            <p>
              We may terminate or suspend your account immediately, without prior notice or liability, for any reason,
              including if you breach these Terms.
            </p>
            <h3>14.3 Effect of Termination</h3>
            <p>Upon termination:</p>
            <ul>
              <li>Your right to use the Service will immediately cease</li>
              <li>We will delete your account data in accordance with Section 5.5</li>
              <li>Provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnification, and limitations of liability</li>
            </ul>
          </section>

          <section>
            <h2>15. Your Data Protection Rights</h2>
            <p>
              If you are located in the United Kingdom or European Economic Area, you have certain rights under the
              UK GDPR and EU GDPR, including:
            </p>
            <ul>
              <li><strong>Right of access</strong>: Request a copy of your personal data</li>
              <li><strong>Right to rectification</strong>: Request correction of inaccurate data</li>
              <li><strong>Right to erasure</strong>: Request deletion of your data in certain circumstances</li>
              <li><strong>Right to restrict processing</strong>: Request that we limit how we use your data</li>
              <li><strong>Right to data portability</strong>: Request your data in a structured, machine-readable format</li>
              <li><strong>Right to object</strong>: Object to processing based on legitimate interests</li>
              <li><strong>Right to withdraw consent</strong>: Where processing is based on consent</li>
            </ul>
            <p>
              To exercise these rights, contact us at{' '}
              <a href="mailto:matt@vennio.app">matt@vennio.app</a>.
              We will respond to your request within one month.
            </p>
            <p>
              You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you
              believe your rights have been violated.
            </p>
          </section>

          <section>
            <h2>16. Changes to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. We will provide notice of material changes by:</p>
            <ul>
              <li>Posting the updated Terms on our website with a new "Last updated" date</li>
              <li>Sending an email to the address associated with your account</li>
            </ul>
            <p>
              Your continued use of the Service after changes become effective constitutes acceptance of the revised
              Terms. If you do not agree to the changes, you must stop using the Service and delete your account.
            </p>
          </section>

          <section>
            <h2>17. General Provisions</h2>
            <h3>17.1 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Vennio
              regarding the Service.
            </p>
            <h3>17.2 Severability</h3>
            <p>
              If any provision of these Terms is held to be invalid, illegal, or unenforceable, the remaining provisions
              shall continue in full force and effect.
            </p>
            <h3>17.3 Waiver</h3>
            <p>
              Our failure to enforce any right or provision of these Terms shall not be deemed a waiver of such right
              or provision.
            </p>
            <h3>17.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms or your rights hereunder without our prior written consent.
              We may assign our rights and obligations without restriction.
            </p>
            <h3>17.5 No Agency</h3>
            <p>
              Nothing in these Terms creates any agency, partnership, joint venture, or employment relationship between
              you and Vennio.
            </p>
          </section>

          <section>
            <h2>18. Governing Law and Disputes</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without
              regard to its conflict of law provisions.
            </p>
            <p>
              Any disputes arising out of or relating to these Terms or the Service shall be subject to the exclusive
              jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2>19. Contact Us</h2>
            <p>If you have any questions about these Terms of Service, please contact us:</p>
            <p><strong>Email</strong>: <a href="mailto:matt@vennio.app">matt@vennio.app</a></p>
            <p><strong>Website</strong>: <a href="https://vennio.app">https://vennio.app</a></p>
          </section>

          <hr />

          <p className={styles.closing}>
            By using Vennio, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>

        </div>
      </div>
    </div>
  );
}
